/**
 * Schémas Zod — Retention / Legal Hold WRITE (Chunk 9.H.3 — human-only).
 *
 * Source DB : migration 0024 (`docs/design/retention-and-sensitive-data.md`).
 *
 * Garde-fous PO (cf. scope spec 9.H.3) :
 *  - PO-Q1 : le handler insère explicitement dans `app.retention_audit_log`
 *            (pas de migration ; colonnes existantes 0024).
 *  - PO-Q2 : tenant_admin avec `tenant_id: null` → 403 côté handler.
 *  - PO-Q3 : désactivation policy via PATCH `{active:false}`. Pas de DELETE.
 *  - PO-Q4 : `legal_minimum_days` non réductible par tenant_admin (handler).
 *  - PO-Q6 : validation référentielle `scope_id` côté handler — `employee_census`
 *            rejeté car table inexistante en 9.H.3.
 *
 * Ces schémas portent UNIQUEMENT la validation de forme (types, bornes,
 * cohérence intra-row). Les règles d'authz (rôle, tenant_admin/super_admin)
 * et la validation référentielle sont dans les handlers — pas ici.
 *
 * IMPORTANT : pas de `retention:write` côté api_key. Les écritures sont
 * human-only ; les routes utilisent `requireRole(["super_admin", ...])`
 * qui refuse `auth.kind === "api_key"` plus tôt.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";
import {
  LegalHoldScopeKindSchema,
  RetentionActionSchema,
  RetentionPolicyBasisSchema,
} from "./retention.js";

// =============================================================================
// Legal holds — POST / PATCH release
// =============================================================================

/**
 * `POST /v1/legal-holds`
 *
 * Règles cohérence :
 *  - `scope_kind = 'tenant'` ⇒ `scope_id` doit être absent ou null
 *  - `scope_kind ≠ 'tenant'` ⇒ `scope_id` requis (UUID)
 *
 * La CHECK DB `legal_holds_scope_id_consistency_chk` re-applique la même règle
 * (double protection applicative + structurelle).
 *
 * `placed_by` n'est PAS dans le body — il est dérivé de `auth.userId` côté
 * handler (un super_admin ne peut pas se faire passer pour quelqu'un d'autre).
 *
 * `status` n'est pas dans le body — toujours `'active'` à la création
 * (default DDL).
 *
 * 9.K.12 (D58.1-b refines schemas) — split base/refined :
 *   `CreateLegalHoldBaseSchema` (sans superRefine) est passé à zValidator côté
 *   route API ; les checks scope_id/scope_kind sont appliqués dans le handler
 *   via `bilingualHttpError("LEGAL_HOLDS_SCOPE_ID_INVALID", 400)` pour porter
 *   le code bilingue ADR 0027 (P-3) au lieu d'un `validation_error` Zod
 *   générique. Les wordings ci-dessous sont en EN technique (P-2 ADR 0027) ;
 *   ils ne servent que de fallback dev. Wording user-facing FR/EN dans
 *   `ERROR_CODES.LEGAL_HOLDS_SCOPE_ID_INVALID`.
 */
export const CreateLegalHoldBaseSchema = z.object({
  scope_kind: LegalHoldScopeKindSchema,
  /** Requis si scope_kind ≠ 'tenant'. */
  scope_id: UuidSchema.nullable().optional(),
  reason: z.string().min(1).max(2000),
  legal_reference: z.string().max(500).nullable().optional(),
});

export const CreateLegalHoldSchema = CreateLegalHoldBaseSchema.superRefine((data, ctx) => {
  if (data.scope_kind === "tenant") {
    if (data.scope_id != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // 9.K.12 — refine technique EN (P-2 ADR 0027).
        message: "scope_id must be null when scope_kind = 'tenant'",
        path: ["scope_id"],
      });
    }
  } else if (data.scope_id == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      // 9.K.12 — refine technique EN (P-2 ADR 0027).
      message: `scope_id is required when scope_kind = '${data.scope_kind}'`,
      path: ["scope_id"],
    });
  }
});
export type CreateLegalHold = z.infer<typeof CreateLegalHoldSchema>;

/**
 * `PATCH /v1/legal-holds/:id/release`
 *
 * `released_by` est dérivé de `auth.userId` côté handler. `released_at` est
 * stampé par le handler (now()).
 */
export const ReleaseLegalHoldSchema = z.object({
  release_reason: z.string().min(1).max(2000),
});
export type ReleaseLegalHold = z.infer<typeof ReleaseLegalHoldSchema>;

// =============================================================================
// Retention policies — POST / PATCH
// =============================================================================

/**
 * `POST /v1/retention-policies`
 *
 * Cohérence :
 *  - `tenant_id`         : null = policy globale (super_admin only — gardé
 *                          handler) ; UUID = override tenant.
 *  - `retention_days`    : null autorisé (= "infini" / "non purgeable").
 *  - `legal_minimum_days`: null autorisé. Si non-null ET `retention_days`
 *                          non-null, alors `legal_minimum_days <= retention_days`
 *                          (CHECK DB `legal_min_le_retention_chk` re-applique).
 *  - `module`            : null autorisé (policy plus large applicable à
 *                          tous les modules de la category).
 *
 * Note unicité : la DB pose un index UNIQUE partiel sur
 * (COALESCE(tenant_id,'00000000-...'), data_category, COALESCE(module,'')).
 * Une violation de cet index renvoie un 409 mappé côté handler.
 */
//
// 9.K.12 (D58.1-b refines schemas) — split base/refined :
//   `CreateRetentionPolicyBaseSchema` (sans superRefine) est passé à zValidator
//   côté route API ; le check legal_minimum ≤ retention est appliqué dans le
//   handler via `bilingualHttpError("RETENTION_POLICY_LEGAL_MINIMUM_EXCEEDS_RETENTION", 400)`
//   pour porter le code bilingue ADR 0027 (P-3). Le wording ci-dessous est en
//   EN technique (P-2 ADR 0027) ; il ne sert que de fallback dev. Wording
//   user-facing FR/EN dans
//   `ERROR_CODES.RETENTION_POLICY_LEGAL_MINIMUM_EXCEEDS_RETENTION`.
export const CreateRetentionPolicyBaseSchema = z.object({
  /** null = policy globale (default). super_admin only — vérifié handler. */
  tenant_id: UuidSchema.nullable().optional(),
  data_category: z.string().min(1).max(120),
  module: z.string().min(1).max(120).nullable().optional(),
  retention_days: z.number().int().nonnegative().nullable(),
  legal_minimum_days: z.number().int().nonnegative().nullable(),
  policy_basis: RetentionPolicyBasisSchema.nullable().optional(),
  action_on_expiry: RetentionActionSchema,
  /** Default DB = true ; on accepte explicite false (création désactivée). */
  active: z.boolean().optional(),
  notes: z.string().max(4000).nullable().optional(),
});

export const CreateRetentionPolicySchema = CreateRetentionPolicyBaseSchema.superRefine(
  (data, ctx) => {
    if (
      data.legal_minimum_days != null &&
      data.retention_days != null &&
      data.legal_minimum_days > data.retention_days
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // 9.K.12 — refine technique EN (P-2 ADR 0027).
        message: "legal_minimum_days cannot exceed retention_days (legal floor inconsistent)",
        path: ["legal_minimum_days"],
      });
    }
  },
);
export type CreateRetentionPolicy = z.infer<typeof CreateRetentionPolicySchema>;

/**
 * `PATCH /v1/retention-policies/:id`
 *
 * Tous les champs métier sont optionnels. Champs structurellement immuables
 * (non éditables) : `id`, `tenant_id`, `data_category`, `module`. Si l'on
 * veut "déplacer" une policy on en crée une nouvelle.
 *
 * Garde-fou PO-Q4 (handler) : `legal_minimum_days` est non réductible par
 * tenant_admin. Vérifié côté handler après lecture de la row courante.
 *
 * Garde-fou PO-Q3 (handler) : `active: false` = désactivation logique. Aucun
 * DELETE applicatif.
 *
 * Cohérence intra-PATCH : si `legal_minimum_days` ET `retention_days` sont
 * tous deux fournis, on applique la même règle qu'à la création. Si un seul
 * des deux est fourni, le check final (vs la valeur stockée) est délégué au
 * handler après lecture de la row courante.
 */
//
// 9.K.12 (D58.1-b refines schemas) — split base/refined :
//   `UpdateRetentionPolicyBaseSchema` (sans superRefine ni refine "≥1 champ")
//   est passé à zValidator côté route API. Les deux checks sont appliqués
//   dans le handler :
//     - "≥1 champ fourni" → `bilingualHttpError("RETENTION_POLICY_NO_FIELDS_TO_UPDATE", 400)`
//     - legal_minimum ≤ retention (intra-PATCH) → `bilingualHttpError("RETENTION_POLICY_LEGAL_MINIMUM_EXCEEDS_RETENTION", 400)`
//   Le check post-PATCH (legal_minimum vs row courante) reste en handler et
//   utilise déjà le même code `RETENTION_POLICY_LEGAL_MINIMUM_EXCEEDS_RETENTION`.
//   Wordings ci-dessous en EN technique (P-2 ADR 0027) — fallback dev seulement.
export const UpdateRetentionPolicyBaseSchema = z.object({
  retention_days: z.number().int().nonnegative().nullable().optional(),
  legal_minimum_days: z.number().int().nonnegative().nullable().optional(),
  policy_basis: RetentionPolicyBasisSchema.nullable().optional(),
  action_on_expiry: RetentionActionSchema.optional(),
  active: z.boolean().optional(),
  notes: z.string().max(4000).nullable().optional(),
});

export const UpdateRetentionPolicySchema = UpdateRetentionPolicyBaseSchema.superRefine(
  (data, ctx) => {
    if (
      data.legal_minimum_days != null &&
      data.retention_days != null &&
      data.legal_minimum_days > data.retention_days
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        // 9.K.12 — refine technique EN (P-2 ADR 0027).
        message: "legal_minimum_days cannot exceed retention_days (legal floor inconsistent)",
        path: ["legal_minimum_days"],
      });
    }
  },
).refine(
  (data) =>
    data.retention_days !== undefined ||
    data.legal_minimum_days !== undefined ||
    data.policy_basis !== undefined ||
    data.action_on_expiry !== undefined ||
    data.active !== undefined ||
    data.notes !== undefined,
  {
    // 9.K.12 — refine technique EN (P-2 ADR 0027).
    message: "At least one field must be provided for PATCH",
    path: ["_body"],
  },
);
export type UpdateRetentionPolicy = z.infer<typeof UpdateRetentionPolicySchema>;
